import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FeeAdjustmentMemberDetail, FeeAdjustmentRecord } from "../types";
export {
  buildMemberFeeMarkupPreview,
  calculateMemberFeeAdjustmentSummary,
  feeMoneyCents,
  feeMoneyText,
  formatFeeAdjustmentAmount,
  formatFeeAdjustmentCompactAmount,
  formatFeeAdjustmentDescription,
  getFeeAdjustmentServiceProject,
  getFeeAdjustmentSalesTotal,
  getMemberFeeAdjustmentAmounts,
  isValidFeeMarkupAmount,
} from "../service";

import {
  completeFeeAdjustment,
  createFeeAdjustmentRequest,
  getFeeAdjustmentEditor,
  getFeeAdjustmentRecordDetail,
  getMemberFeeAdjustmentDetail,
  listWorkspaceFeeAdjustmentRecords,
  listWorkspaceFeeAdjustmentRequests,
  listRosterFeeAdjustmentRequests,
  previewFeeAdjustment,
  updateFeeAdjustmentSalesAmount,
} from "../repo";

const root = ["fee-adjustment"] as const;

/** 弹窗按工作区和记录隔离缓存，重新打开时取得当前保存结果。 */
export function useFeeAdjustmentRecordDetail(workspaceId: string, requestId: string) {
  return useQuery({
    queryKey: [...root, "record-detail", workspaceId, requestId],
    queryFn: ({ signal }) => getFeeAdjustmentRecordDetail(workspaceId, requestId, signal),
    retry: false,
    refetchOnMount: "always",
  });
}

/** 成员详情使用独立缓存，避免与名单记录数组混用。 */
export function useMemberFeeAdjustmentQuery(projectId: string, rosterId: string, memberId: string, customerQuoteId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...root, "member", rosterId, projectId, memberId, customerQuoteId ?? "selected"],
    queryFn: () => getMemberFeeAdjustmentDetail(projectId, rosterId, memberId, customerQuoteId),
    enabled,
    retry: false,
  });
}

/**
 *
 */
export function useRosterFeeAdjustmentRequests(
  rosterId: string,
  enabled: boolean,
  customerQuoteId?: string,
) {
  return useQuery({
    queryKey: [...root, "roster", rosterId, customerQuoteId ?? "all"],
    queryFn: () => listRosterFeeAdjustmentRequests(rosterId, customerQuoteId),
    enabled: enabled && Boolean(rosterId),
  });
}

/**
 *
 */
export function useCreateFeeAdjustmentRequest(rosterId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: Parameters<typeof createFeeAdjustmentRequest>[1]) =>
      createFeeAdjustmentRequest(rosterId, command),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...root, "roster", rosterId] }),
  });
}

/**
 *
 */
export function useUpdateFeeAdjustmentSalesAmount(rosterId: string, requestId: string) {
  const queryClient = useQueryClient();
  const mutationKey = [...root, "sales-amount", requestId];
  const pendingCount = useIsMutating({ mutationKey });
  const mutation = useMutation({
    mutationKey,
    mutationFn: (command: Parameters<typeof updateFeeAdjustmentSalesAmount>[1]) =>
      updateFeeAdjustmentSalesAmount(requestId, command),
    onSuccess: async (record) => {
      queryClient.setQueriesData<FeeAdjustmentRecord[]>(
        { queryKey: [...root, "roster", rosterId] },
        (current) => current?.map((item) => item.request_id === record.request_id && item.customer_quote_id === record.customer_quote_id ? record : item),
      );
      queryClient.setQueriesData<FeeAdjustmentMemberDetail>(
        { queryKey: [...root, "member", rosterId] },
        (current) => current && current.order_id === record.order_id && current.customer_quote_id === record.customer_quote_id
          ? { ...current, records: current.records.map((item) => item.request_id === record.request_id ? record : item) }
          : current,
      );
      await queryClient.invalidateQueries({ queryKey: [...root, "workspace", record.fulfillment_workspace_id] });
      await queryClient.invalidateQueries({ queryKey: [...root, "editor", record.request_id] });
      await queryClient.invalidateQueries({ queryKey: [...root, "record-detail", record.fulfillment_workspace_id, record.request_id] });
    },
  });
  return { ...mutation, isPending: mutation.isPending || pendingCount > 0 };
}

/**
 *
 */
export function useWorkspaceFeeAdjustmentRequests(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...root, "workspace", workspaceId, "requests"],
    queryFn: () => listWorkspaceFeeAdjustmentRequests(workspaceId),
    enabled,
  });
}

/**
 *
 */
export function useWorkspaceFeeAdjustmentRecords(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...root, "workspace", workspaceId, "records"],
    queryFn: () => listWorkspaceFeeAdjustmentRecords(workspaceId),
    enabled,
  });
}

/**
 *
 */
export function useFeeAdjustmentEditor(requestId: string | null) {
  return useQuery({
    queryKey: [...root, "editor", requestId],
    queryFn: () => getFeeAdjustmentEditor(requestId!),
    enabled: Boolean(requestId),
  });
}

/**
 *
 */
export function usePreviewFeeAdjustment(requestId: string | null) {
  return useMutation({
    mutationFn: (command: Parameters<typeof previewFeeAdjustment>[1]) =>
      previewFeeAdjustment(requestId!, command),
  });
}

/**
 *
 */
export function useCompleteFeeAdjustment(workspaceId: string, requestId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...root, "complete", workspaceId, requestId],
    mutationFn: (command: Parameters<typeof completeFeeAdjustment>[1]) =>
      completeFeeAdjustment(requestId!, command),
    onSuccess: async (record) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...root, "roster", record.roster_id] }),
        queryClient.invalidateQueries({ queryKey: [...root, "member", record.roster_id] }),
        queryClient.invalidateQueries({ queryKey: [...root, "record-detail", record.fulfillment_workspace_id] }),
        queryClient.invalidateQueries({ queryKey: [...root, "workspace", record.fulfillment_workspace_id] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "workspace", record.fulfillment_workspace_id] }),
        queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "list"] }),
      ]);
    },
  });
}
